import React from "react";
import { useIntl } from "react-intl";

import { ColumnProps } from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable";
import { SocialSecurityNumber } from "src/common/components/SocialSecurityNumber";
import { StatusBadge } from "src/common/components/StatusBadge";
import { messages } from "src/modules/ktx/patient-list/messages";
import {
	PatientStatus,
	WaitingListTime
} from "src/modules/ktx/patient-list/types";
import {
	compareDates,
	compareNumberEvents,
	mapNumberEventToString,
	renderNumberEvent
} from "src/modules/shared/patient-list";

export function useDesktopColumnProps(): ColumnProps[] {
	const status = useStatusColumnDesktop();
	const socialSecurityNumber = useSocialSecurityNumberColumn();
	const name = useNameColumn();
	const dialysisCentre = useDialysisCentreColumn();
	const weight = useWeightColumn();
	const bloodGroup = useBloodGroupColumn();

	const waitingList = useWaitingListColumnDesktop();
	const assignedUser = useAssignedUserColumn();
	const assignTime = useAssignTimeColumn();
	const statusByNephrologist = useStatusByNephrologist();
	const modifTime = useModifTime();

	return React.useMemo(
		() => [
			modifTime,
			name,
			status,
			socialSecurityNumber,
			weight,
			bloodGroup,
			waitingList,
			assignedUser,
			assignTime,
			statusByNephrologist,
			dialysisCentre
		],
		[
			status,
			socialSecurityNumber,
			name,
			dialysisCentre,
			weight,
			bloodGroup,
			waitingList,
			assignedUser,
			assignTime,
			statusByNephrologist,
			modifTime
		]
	);
}

export function useMobileColumnProps(): MobileColumnProps {
	const modifTime = useModifTime();
	const name = useNameColumn();
	const dialysisCentre = useDialysisCentreColumn();
	const status = useStatusColumnMobile();
	const socialSecurityNumber = useSocialSecurityNumberColumn();

	const bloodGroup = useBloodGroupColumn();
	const waitingList = useWaitingListColumnMobile();
	const weight = useWeightColumn();
	const assignedUser = useAssignedUserColumn();
	const assignTime = useAssignTimeColumn();
	const statusByNephrologist = useStatusByNephrologist();

	return React.useMemo(
		() => ({
			icon: status,
			title: name,
			subtitle: socialSecurityNumber,
			fix: [
				modifTime,
				dialysisCentre,
				bloodGroup,
				waitingList,
				weight,
				assignedUser,
				assignTime,
				statusByNephrologist
			],
			optional: []
		}),
		[
			modifTime,
			dialysisCentre,
			status,
			name,
			socialSecurityNumber,
			bloodGroup,
			waitingList,
			weight,
			assignedUser,
			assignTime,
			statusByNephrologist
		]
	);
}

function useStatusColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, longLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={longLabel} />;
			},
			mapToString({ longLabel }: PatientStatus): string {
				return longLabel;
			}
		}),
		[formatMessage]
	);
}

function useStatusColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, shortLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={shortLabel} />;
			},
			mapToString({ shortLabel }: PatientStatus): string {
				return shortLabel;
			}
		}),
		[formatMessage]
	);
}

function useNameColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "name",
			label: formatMessage(messages.name)
		}),
		[formatMessage]
	);
}

function useSocialSecurityNumberColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "socialSecurityNumber",
			label: formatMessage(messages.socialSecurityNumber),
			render(value: string): React.ReactNode {
				return <SocialSecurityNumber>{value}</SocialSecurityNumber>;
			}
		}),
		[formatMessage]
	);
}

function useStatusByNephrologist(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "statusByNephrologist",
			label: formatMessage(messages.statusByNephrologist)
		}),
		[formatMessage]
	);
}

function useDialysisCentreColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "dialysisCentre",
			label: formatMessage(messages.dialysisCentre)
		}),
		[formatMessage]
	);
}

function useModifTime(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "modifTime",
			label: formatMessage(messages.modifTime),
			compare: compareDates
		}),
		[formatMessage]
	);
}

function useAssignedUserColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "assignedUser",
			label: formatMessage(messages.assignedUserLabel)
		}),
		[formatMessage]
	);
}

function useAssignTimeColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "assignTime",
			label: formatMessage(messages.assignTime),
			compare: compareDates
		}),
		[formatMessage]
	);
}

function useWeightColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "weight",
			label: formatMessage(messages.weight),
			render: renderNumberEvent,
			mapToString: mapNumberEventToString,
			compare: compareNumberEvents
		}),
		[formatMessage]
	);
}

function useBloodGroupColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "bloodGroup",
			label: formatMessage(messages.bloodGroup)
		}),
		[formatMessage]
	);
}

function useWaitingListColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListTime",
			label: formatMessage(messages.waitingListLabel),
			render(value: WaitingListTime | undefined): React.ReactNode {
				if (value === undefined) {
					return null;
				}
				if (value.months > 1) {
					return (
						<div>
							<b>{Math.round(value.months)}</b>
							{". "}
							<i>
								{formatMessage(
									messages.waitingListMonthsUnitDesktop
								)}
							</i>
						</div>
					);
				} else {
					return (
						<div>
							<b>{value.days}</b>{" "}
							<i>
								{formatMessage(
									messages.waitingListDaysUnitDesktop
								)}
							</i>
						</div>
					);
				}
			},
			mapToString(value: WaitingListTime | undefined): string {
				if (value && value.months > 1) {
					return `${value.months} ${formatMessage(
						messages.waitingListMonthsUnitDesktop
					)}`;
				} else {
					return `${value ? value.days : "-"} ${formatMessage(
						messages.waitingListDaysUnitDesktop
					)}`;
				}
			},
			compare(
				a: WaitingListTime | undefined,
				b: WaitingListTime | undefined
			): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a.days < b.days) {
					return -1;
				}
				if (a.days > b.days) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useWaitingListColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "waitingListTime",
			label: formatMessage(messages.waitingListLabel),
			render(value: WaitingListTime | undefined): React.ReactNode {
				if (value === undefined) {
					return "-";
				}

				if (value.months > 1) {
					return (
						<div>
							<b>{Math.round(value.months)}</b>
							{". "}
							<i>
								{formatMessage(
									messages.waitingListMonthsUnitMobile
								)}
							</i>
						</div>
					);
				} else {
					return (
						<div>
							<b>{value.days}</b>{" "}
							<i>
								{formatMessage(
									messages.waitingListDaysUnitMobile
								)}
							</i>
						</div>
					);
				}
			},
			mapToString(value: WaitingListTime | undefined): string {
				if (value && value.months > 1) {
					return `${value.months} ${formatMessage(
						messages.waitingListMonthsUnitDesktop
					)}`;
				} else {
					return `${value ? value.days : "-"} ${formatMessage(
						messages.waitingListDaysUnitDesktop
					)}`;
				}
			},
			compare(
				a: WaitingListTime | undefined,
				b: WaitingListTime | undefined
			): number {
				if (a === undefined && b === undefined) {
					return 0;
				}
				if (a === undefined) {
					return 1;
				}
				if (b === undefined) {
					return -1;
				}
				if (a.days < b.days) {
					return -1;
				}
				if (a.days > b.days) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}
